import {
  formatIsoDate,
  normalizeSampleType,
  normalizeSamplingPointSubType,
  parseCoordinatesCell,
  parseDateCell,
  parseTextCell,
} from '../sample-sheet';
import { getOrgUniqueSamplingPointKey } from '../sampling-points';
import { BioRep, RawSamplingPoint } from '../types';
import type { SampleSheetDraftRow } from './types';
import { isRowEmpty } from './validation';

const groupKeySeparator = '###';
const bioRepSequence = Object.values(BioRep) as BioRep[];
const bioRepSet = new Set(bioRepSequence);
const whitespaceCollapsePattern = /\s+/g;

function getRowOrderIndex(row: SampleSheetDraftRow, fallbackIndex: number): number {
  const candidate = row.rowIndex;
  if (typeof candidate === 'number' && Number.isFinite(candidate)) {
    return candidate;
  }
  return fallbackIndex;
}

export function buildGroupKey(row: SampleSheetDraftRow): string | null {
  const environmentKey = buildSamplingPointKey(row);
  if (!environmentKey) {
    return null;
  }
  const dateKey = getDateKey(row.sampleCollectionDate);
  return [environmentKey, dateKey].join(groupKeySeparator);
}

function buildSamplingPointKey(row: SampleSheetDraftRow): string | null {
  const sampleDescriptionRaw = parseTextCell(row.sampleDescription) ?? null;
  const sampleDescription = sampleDescriptionRaw
    ? sampleDescriptionRaw.replace(whitespaceCollapsePattern, ' ').trim()
    : null;

  const normalizedType = normalizeSampleType(row.sampleType);
  const sampleType = typeof normalizedType === 'undefined' ? null : normalizedType;
  if (!sampleDescription || !sampleType) {
    return null;
  }

  const normalizedSubtype = normalizeSamplingPointSubType(row.detailedSampleType, sampleType);
  const detailedSampleType = typeof normalizedSubtype === 'undefined' ? null : normalizedSubtype ?? null;
  const country = parseTextCell(row.country) ?? null;
  const stateOrRegion = parseTextCell(row.stateOrRegion) ?? null;
  const city = parseTextCell(row.city) ?? null;
  const normalizedCoordinates = parseCoordinatesCell(row.latitudeLongitude);
  const coordinates = normalizedCoordinates === undefined ? null : normalizedCoordinates;

  const rawSamplingPoint: RawSamplingPoint = {
    name: sampleDescription,
    type: sampleType,
    subtype: detailedSampleType,
    country,
    region: stateOrRegion,
    city,
    lat: coordinates?.lat ?? null,
    lon: coordinates?.lon ?? null,
    inferredLat: null,
    inferredLon: null,
    adminLevels: null,
  };

  return getOrgUniqueSamplingPointKey(rawSamplingPoint);
}

type RowMeta = {
  row: SampleSheetDraftRow;
  key: string | null;
  createdAt: number;
  originalSampleNumber: number | null;
  originalBioReplica: BioRep | null;
  isLocked: boolean;
  orderIndex: number;
};

type RowUpdates = {
  sampleNumber?: number | null;
  bioReplica?: BioRep | null;
};

export type DeriveSheetOptions = {
  legacyBioReplicates?: boolean;
  firstSampleNumber?: number;
};

export function deriveSheet(rows: SampleSheetDraftRow[], options?: DeriveSheetOptions): SampleSheetDraftRow[] {
  if (rows.length === 0) {
    return rows;
  }

  const legacyBioReplicates = options?.legacyBioReplicates ?? true;
  const firstSampleNumber = normalizeFirstSampleNumber(options?.firstSampleNumber);

  if (!legacyBioReplicates) {
    return deriveNonLegacySheet(rows, firstSampleNumber);
  }

  return deriveLegacySheet(rows, firstSampleNumber);
}

function deriveNonLegacySheet(rows: SampleSheetDraftRow[], firstSampleNumber: number): SampleSheetDraftRow[] {
  const metas = rows.map<RowMeta>((row, index) => {
    const createdAt = toTimestamp(row, index);
    return {
      row,
      key: null,
      createdAt,
      originalSampleNumber: toNumber(row.sampleNumber),
      originalBioReplica: normalizeBioRepDraft(row.bioReplica),
      isLocked: Boolean(row.isReadOnly || row.sampleId != null),
      orderIndex: getRowOrderIndex(row, index),
    } satisfies RowMeta;
  });

  const maxLockedNumber = metas.reduce((max, meta) => {
    if (!meta.isLocked || meta.originalSampleNumber == null) {
      return max;
    }
    return Math.max(max, meta.originalSampleNumber);
  }, 0);

  const orderedEditable = metas
    .filter(meta => !meta.isLocked)
    .slice()
    .sort((left, right) => {
      if (left.orderIndex !== right.orderIndex) {
        return left.orderIndex - right.orderIndex;
      }
      if (left.createdAt !== right.createdAt) {
        return left.createdAt - right.createdAt;
      }
      return left.row.rowId.localeCompare(right.row.rowId);
    });

  const startNumber = maxLockedNumber > 0 ? maxLockedNumber + 1 : firstSampleNumber;
  const updatesByRowId = new Map<string, RowUpdates>();

  orderedEditable.forEach((meta, index) => {
    const desiredSampleNumber = isEffectivelyEmpty(meta.row) ? null : startNumber + index;
    const updates: RowUpdates = {};
    let mutated = false;

    if (meta.originalSampleNumber !== desiredSampleNumber) {
      updates.sampleNumber = desiredSampleNumber;
      mutated = true;
    }

    if (meta.originalBioReplica != null) {
      updates.bioReplica = null;
      mutated = true;
    }

    if (mutated) {
      updatesByRowId.set(meta.row.rowId, updates);
    }
  });

  if (updatesByRowId.size === 0) {
    return rows;
  }

  return rows.map(row => {
    const updates = updatesByRowId.get(row.rowId);
    if (!updates) {
      return row;
    }
    return {
      ...row,
      sampleNumber: Object.prototype.hasOwnProperty.call(updates, 'sampleNumber')
        ? updates.sampleNumber ?? null
        : row.sampleNumber,
      bioReplica: Object.prototype.hasOwnProperty.call(updates, 'bioReplica') ? updates.bioReplica ?? null : row.bioReplica,
    } satisfies SampleSheetDraftRow;
  });
}

function deriveLegacySheet(rows: SampleSheetDraftRow[], firstSampleNumber: number): SampleSheetDraftRow[] {
  const metaByRowId = new Map<string, RowMeta>();
  const metas = rows.map<RowMeta>((row, index) => {
    const createdAt = toTimestamp(row, index);
    const baseKey = buildGroupKey(row);
    const key = baseKey;

    const meta = {
      row,
      key,
      createdAt,
      originalSampleNumber: toNumber(row.sampleNumber),
      originalBioReplica: normalizeBioRepDraft(row.bioReplica),
      isLocked: Boolean(row.isReadOnly || row.sampleId != null),
      orderIndex: getRowOrderIndex(row, index),
    } satisfies RowMeta;
    metaByRowId.set(row.rowId, meta);
    return meta;
  });

  const groups = new Map<string, RowMeta[]>();

  metas.forEach(meta => {
    if (!meta.key) {
      return;
    }
    const bucket = groups.get(meta.key);
    if (bucket) {
      bucket.push(meta);
      return;
    }
    groups.set(meta.key, [meta]);
  });

  const groupEntries = Array.from(groups.entries()).map(([key, members]) => {
    const firstSeen = members.reduce((min, meta) => Math.min(min, meta.createdAt), Number.POSITIVE_INFINITY);
    const firstOrderIndex = members.reduce((min, meta) => Math.min(min, meta.orderIndex), Number.POSITIVE_INFINITY);
    const lockedNumber =
      members.find(member => member.isLocked && member.originalSampleNumber != null)?.originalSampleNumber ?? null;
    const sortedMembers = [...members].sort((left, right) => {
      if (left.createdAt !== right.createdAt) {
        return left.createdAt - right.createdAt;
      }
      return left.orderIndex - right.orderIndex;
    });
    return {
      key,
      members: sortedMembers,
      firstSeen,
      firstOrderIndex,
      lockedNumber,
    };
  });

  // Primary ordering by sheet ordering (rowIndex/orderIndex) so numbering is stable
  // as users fill rows top-to-bottom. createdAt is used only as a secondary hint.
  groupEntries.sort((left, right) => {
    if (left.firstOrderIndex !== right.firstOrderIndex) {
      return left.firstOrderIndex - right.firstOrderIndex;
    }
    if (left.firstSeen !== right.firstSeen) {
      return left.firstSeen - right.firstSeen;
    }
    // Just to be sure, use bioGroup+date key as a tiebreaker use key to make sorting deterministic (needed?)
    return left.key.localeCompare(right.key);
  });

  const reservedNumbers = new Set<number>();
  const groupNumber = new Map<string, number>();

  groupEntries.forEach(entry => {
    if (entry.lockedNumber != null) {
      groupNumber.set(entry.key, entry.lockedNumber);
      reservedNumbers.add(entry.lockedNumber);
    }
  });

  const maxReserved = reservedNumbers.size > 0 ? Math.max(...reservedNumbers) : 0;
  let nextNumber = maxReserved > 0 ? maxReserved + 1 : firstSampleNumber;

  groupEntries.forEach(entry => {
    if (groupNumber.has(entry.key)) {
      return;
    }

    while (reservedNumbers.has(nextNumber)) {
      nextNumber += 1;
    }
    groupNumber.set(entry.key, nextNumber);
    reservedNumbers.add(nextNumber);
    nextNumber += 1;
  });

  const rowUpdates = new Map<string, RowUpdates>();

  groupEntries.forEach(entry => {
    let replicaIndex = 0;

    entry.members.forEach(member => {
      const updates: RowUpdates = { ...rowUpdates.get(member.row.rowId) };
      let mutated = false;
      const targetSampleNumber = member.isLocked ? member.originalSampleNumber : groupNumber.get(entry.key) ?? null;
      if (!member.isLocked) {
        if (member.originalSampleNumber !== targetSampleNumber) {
          updates.sampleNumber = targetSampleNumber ?? null;
          mutated = true;
        }
      }

      if (!member.isLocked) {
        replicaIndex += 1;
        const desiredReplicaRaw = getBioRepByIndex(replicaIndex);
        if (desiredReplicaRaw !== member.originalBioReplica) {
          updates.bioReplica = desiredReplicaRaw;
          mutated = true;
        }
      } else if (member.originalBioReplica) {
        replicaIndex = Math.max(replicaIndex, getBioRepIndex(member.originalBioReplica));
      }

      if (mutated) {
        rowUpdates.set(member.row.rowId, updates);
      } else {
        rowUpdates.delete(member.row.rowId);
      }
    });
  });

  return rows.map(row => {
    const updates = rowUpdates.get(row.rowId);
    const meta = metaByRowId.get(row.rowId);
    const shouldClearDerivedFields = Boolean(meta && !meta.isLocked && (meta.key == null || isEffectivelyEmpty(meta.row)));

    if (!updates && !shouldClearDerivedFields) {
      return row;
    }

    const next: SampleSheetDraftRow = { ...row };
    if (updates) {
      if (Object.prototype.hasOwnProperty.call(updates, 'sampleNumber')) {
        next.sampleNumber = updates.sampleNumber ?? null;
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'bioReplica')) {
        next.bioReplica = updates.bioReplica ?? null;
      }
    }

    if (shouldClearDerivedFields) {
      next.sampleNumber = null;
      next.bioReplica = null;
    }

    return next;
  });
}

function isEffectivelyEmpty(row: SampleSheetDraftRow): boolean {
  if (isRowEmpty(row)) {
    return true;
  }
  const sanitized: SampleSheetDraftRow = { ...row, sampleNumber: null, bioReplica: null };
  return isRowEmpty(sanitized);
}

function toNumber(raw: SampleSheetDraftRow[keyof SampleSheetDraftRow]): number | null {
  if (raw == null) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeFirstSampleNumber(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
    return 1;
  }
  return value;
}

function getDateKey(value: SampleSheetDraftRow['sampleCollectionDate']): string {
  const parsed = parseDateCell(value);
  if (!parsed) {
    return '';
  }
  return formatIsoDate(parsed);
}

function toTimestamp(row: SampleSheetDraftRow, fallbackIndex: number): number {
  const candidate = row.createdAt ?? row.sampleCollectionDate;
  if (candidate instanceof Date) {
    const time = candidate.getTime();
    return Number.isNaN(time) ? fallbackIndex : time;
  }
  if (typeof candidate === 'string') {
    const parsed = Date.parse(candidate);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  const orderIndex = getRowOrderIndex(row, fallbackIndex);
  return Number.isFinite(orderIndex) ? orderIndex : fallbackIndex;
}

function normalizeBioRepDraft(value: SampleSheetDraftRow['bioReplica']): BioRep | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    if (bioRepSet.has(trimmed as BioRep)) {
      return trimmed as BioRep;
    }
    const upper = trimmed.toUpperCase();
    if (bioRepSet.has(upper as BioRep)) {
      return upper as BioRep;
    }
    return null;
  }
  return bioRepSet.has(value) ? value : null;
}

function getBioRepByIndex(index: number): BioRep | null {
  const candidate = bioRepSequence[index - 1];
  return typeof candidate === 'undefined' ? null : candidate;
}

function getBioRepIndex(replica: BioRep | null): number {
  if (!replica) {
    return 0;
  }
  const index = bioRepSequence.indexOf(replica);
  return index === -1 ? 0 : index + 1;
}
